/**
 * My Cispracs ID notification settings, as the server holds them. They are a
 * ceiling over this device's switches (shared rule: @bunker/shared/notifyPrefs
 * `accountAllows`): they can stop a card or a sound, never start one. The
 * device switches themselves (muteSettings, uiPrefs, the OS banner switch) are
 * never written from here.
 *
 * They arrive over the socket only — in `ready.notifyPrefs` and as the
 * `me:notify-prefs` event (lib/socketEvents.ts). The client makes no request of
 * its own, so an idle or hidden tab costs nothing; the server refreshes them for
 * connected people (server/src/lib/accountNotifyPrefs.ts) and also retries a
 * failed first fetch there. A `ready` without the field, or with
 * `enabled: false`, means "off": nothing is applied.
 *
 * The copy belongs to the account it arrived for: after a sign-out or an
 * account switch it is dropped and ignored until the new account's `ready`.
 */
import type { AccountNotifyPrefs, AccountNotifyPrefsState } from '@bunker/shared/notifyPrefs';
import { parseAccountNotifyPrefs } from '@bunker/shared/notifyPrefs';
import { useAuthStore } from '@/stores/authStore';

interface Held {
  userId: string;
  prefs: AccountNotifyPrefs | null;
}

let held: Held | null = null;

function myId(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

/** The account settings for the signed-in person, or null for "no account restriction". */
export function currentAccountNotifyPrefs(): AccountNotifyPrefs | null {
  const id = myId();
  if (!held || !id || held.userId !== id) return null;
  return held.prefs;
}

/**
 * Take what the server sent for `userId` (`ready` names the account; an event
 * goes to my user room, so it is mine). Off or missing → nothing applied. A
 * `null` (the server has no answer yet) or unreadable `prefs` keeps the copy
 * already held for this account.
 */
export function applyAccountNotifyPrefs(userId: string | null, state: AccountNotifyPrefsState | undefined): void {
  if (!userId || userId !== myId()) return;
  if (!state?.enabled) {
    held = null;
    return;
  }
  const prefs = state.prefs === null ? null : parseAccountNotifyPrefs(state.prefs);
  if (prefs) held = { userId, prefs };
  else if (held?.userId !== userId) held = { userId, prefs: null };
}

/** Sign-out, account switch, socket torn down: forget the copy. */
export function resetAccountNotifyPrefs(): void {
  held = null;
}
